import { useQuery } from '@tanstack/react-query';
import api from '../../../services';
import { fetchAll } from '../../../services/helpers';
import { EXCLUDED_OFFER_TYPE_IDS, type Offer } from './sequenceModel';

/* eslint-disable @typescript-eslint/no-explicit-any -- API entities are untyped across the app */
const byName = (list: any[] = []) =>
  [...list].sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? '')));

/** Loaded once per visit: an editor must not have its form re-initialised under it by a refetch. */
const ONCE = { refetchOnWindowFocus: false, refetchOnReconnect: false, staleTime: Infinity } as const;

export const useSequenceView = (sequenceId: number | null) =>
  useQuery({
    queryKey: ['sequenceBuilder', 'sequenceView', sequenceId],
    queryFn: () => api.get(`sequences/sequenceView/${sequenceId}`).then((res) => res.data),
    enabled: sequenceId != null,
    ...ONCE,
    cacheTime: 0,
  });

export const useSequencePublished = (sequenceId: number | null) =>
  useQuery({
    queryKey: ['sequenceBuilder', 'published', sequenceId],
    queryFn: () => api.get(`sequences/publish/${sequenceId}`).then((res) => res.data),
    enabled: sequenceId != null,
    ...ONCE,
    cacheTime: 0,
  });

/** Primary-variant offers, as the classic editor's list loads them (`offers/table`). */
export const useBuilderOffers = () =>
  useQuery({
    queryKey: ['sequenceBuilder', 'offers'],
    queryFn: async () => {
      const offers: Offer[] = await fetchAll('offers/table');
      return offers
        .filter((offer) => !EXCLUDED_OFFER_TYPE_IDS.includes(offer.offerType?.id as number))
        .sort((a, b) => b.id - a.id);
    },
    refetchOnWindowFocus: false,
  });

/** Every primary offer's variations, keyed by primary offer id; drives the live/paused chips. */
export const useOfferVariationsMap = () =>
  useQuery({
    queryKey: ['sequenceBuilder', 'variationsMap'],
    queryFn: () => api.get('offervariations/all-variations-map').then((res) => res.data ?? {}),
    refetchOnWindowFocus: false,
  });

export const useSitesList = () =>
  useQuery({
    queryKey: ['sequenceBuilder', 'sites'],
    queryFn: () => api.get('sites').then((res) => res.data),
    select: (data: any[]) =>
      [...(data ?? [])].sort((a, b) => String(a?.domain ?? '').localeCompare(String(b?.domain ?? ''))),
    refetchOnWindowFocus: false,
  });

export const useLayoutsList = () =>
  useQuery({
    queryKey: ['sequenceBuilder', 'layouts'],
    queryFn: () => api.get('layouts/table').then((res) => res.data),
    select: byName,
    refetchOnWindowFocus: false,
  });

export const useContainersList = () =>
  useQuery({
    queryKey: ['sequenceBuilder', 'containers'],
    queryFn: () => api.get('containers/table').then((res) => res.data),
    select: byName,
    refetchOnWindowFocus: false,
  });

/** Offer types keyed by name, which is how template settings and container hints refer to them. */
export const useOfferTypesByName = () =>
  useQuery({
    queryKey: ['sequenceBuilder', 'offerTypes'],
    queryFn: () => api.get('offertypes').then((res) => res.data),
    select: (data: any[]) => Object.fromEntries((data ?? []).map((type) => [type.name, type])) as Record<string, any>,
    refetchOnWindowFocus: false,
  });

/** A block's member offers, for a block whose summary row did not carry them. */
export const useOfferBlockByOfferId = (offerId: number | null) =>
  useQuery({
    queryKey: ['sequenceBuilder', 'offerBlock', offerId],
    queryFn: () => api.get(`offerblocks/offer/${offerId}`).then((res) => res.data),
    enabled: offerId != null,
    refetchOnWindowFocus: false,
  });
